import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { Chip, Typography, Button } from "@mui/material";
import avatar from "../../../../../../../../../../assets/avathar.jpg";

const RealTimeServiceManDataGrid = ({ 
  franchiseId, 
  websocketUrl, 
  setRealTimeWorkersCount, 
  tokenKey, 
  initialWorkers // This prop should contain the full worker list from parent
}) => { 
  const [realTimeWorkers, setRealTimeWorkers] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("Connecting...");

  // --- 1. Initialize state with full list from API ---
  useEffect(() => {
    if (initialWorkers && initialWorkers.length > 0) {
      // Store the complete worker details
      setRealTimeWorkers(initialWorkers);
      setRealTimeWorkersCount(initialWorkers.length);
      console.log("✅ Initialized with full worker list:", initialWorkers.length);
    }
  }, [initialWorkers, setRealTimeWorkersCount]);

  // --- 2. WebSocket Connection Logic ---
  useEffect(() => {
    const token = localStorage.getItem(tokenKey);

    if (!token) {
      console.error("❌ Authentication Token is missing.");
      setConnectionStatus("Authentication Error");
      return;
    }

    const wsUrl = websocketUrl.endsWith("/")
      ? websocketUrl
      : `${websocketUrl}/`;
      
    const wsUrlWithToken = `${wsUrl}?token=${token}`;

    console.log("🔌 Connecting to Worker WebSocket:", wsUrlWithToken);

    const ws = new WebSocket(wsUrlWithToken);

    ws.onopen = () => {
      console.log("✅ Worker WebSocket Connected successfully");
      setConnectionStatus("Connected");
      
      // Send subscription request
      const subscribeMessage = {
          type: "subscribe.worker.status", 
          business_id: franchiseId
      };
      
      try {
          ws.send(JSON.stringify(subscribeMessage));
          console.log("📢 Sent subscription request:", subscribeMessage);
      } catch (e) {
          console.error("Failed to send subscription message:", e);
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("🔥 WebSocket Update Received:", JSON.stringify(data, null, 2)); 

        let updatePayload = null;

        // Handle worker_status_updated message type (your actual WebSocket format)
        if (data.type === 'worker_status_updated' && data.worker_id) {
          // Map the WebSocket data to match our table structure
          updatePayload = {
            id: data.worker_id,
            worker_name: data.worker_name,
            mobile_number: data.mobile_number,
            email: data.email,
            address: data.address,
            location: data.location,
            district: data.district,
            postal_name: data.postal_name,
            zipnumber: data.zipnumber,
            worker_image: data.worker_image,
            experience: data.experience,
            is_available: data.is_available,
            is_suspended: data.is_suspended !== undefined ? data.is_suspended : false,
            // Extract services properly
            service_name: data.services ? data.services.map(s => s.service).join(', ') : '',
            service: data.services || [],
            // Extract category from first service
            category_name: data.services && data.services.length > 0 ? data.services[0].category_id : '',
            // Extract geo_location
            latitude: data.geo_location?.latitude,
            longitude: data.geo_location?.longitude,
          };
          console.log("✅ Mapped WebSocket data:", updatePayload);
        }
        // Handle other message types if needed
        else if (data.type === 'worker_update' || data.type === 'worker_location_update' || data.type === 'new_worker') {
            updatePayload = data.data || data.worker || data;
        }

        if (updatePayload && updatePayload.id) {
          
          // Merge status updates with existing worker details
          setRealTimeWorkers((prevWorkers) => {
            const existingIndex = prevWorkers.findIndex(
              (w) => w.id === updatePayload.id
            );
            
            let updatedWorkers;

            if (existingIndex !== -1) {
              // Worker exists - merge the update with existing full details
              updatedWorkers = prevWorkers.map((worker, index) =>
                index === existingIndex 
                  ? { ...worker, ...updatePayload } // Keep all existing fields, update only changed ones
                  : worker
              );
              console.log(`✅ Updated worker ${updatePayload.id}:`, updatedWorkers[existingIndex]);
            } else {
              // New worker - add to beginning of list
              updatedWorkers = [updatePayload, ...prevWorkers];
              console.log(`⚠️ New worker added:`, updatePayload);
            }

            setRealTimeWorkersCount(updatedWorkers.length);
            return updatedWorkers;
          });
        } else {
          console.warn("⚠️ Invalid WebSocket payload - missing worker_id:", data);
        }
      } catch (error) {
        console.error("❌ Error parsing Worker WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("❌ Worker WebSocket Error:", error);
      setConnectionStatus("Error");
    };

    ws.onclose = (event) => {
      console.log("🔌 Worker WebSocket Disconnected", event.code, event.reason);
      setConnectionStatus("Disconnected");
    };

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [franchiseId, websocketUrl, setRealTimeWorkersCount, tokenKey]);

  // --- Data Grid Logic ---
  const filteredWorkers = realTimeWorkers?.filter(w => w.is_available === true) || [];
  const rows = filteredWorkers?.map((worker, index) => {
    // Handle service name from different possible formats
    let serviceName = '';
    if (worker.service_name) {
      serviceName = worker.service_name;
    } else if (worker.services && Array.isArray(worker.services)) {
      serviceName = worker.services.map(s => s.service || s.service_name || s.name).join(', ');
    } else if (worker.service && Array.isArray(worker.service)) {
      serviceName = worker.service.map(s => s.service_name || s.name).join(', ');
    }

    // Handle location coordinates from different formats
    const latitude = worker.latitude || worker.current_latitude || worker.geo_location?.latitude;
    const longitude = worker.longitude || worker.current_longitude || worker.geo_location?.longitude;

    return {
      id: worker.id,
      displayIndex: index + 1,
      worker_image: worker.worker_image,
      worker_name: worker.worker_name,
      mobile_number: worker.mobile_number,
      service_name: serviceName,
      category_name: worker.category_name,
      experience: worker.experience,
      address: worker.address,
      location: worker.location,
      district: worker.district,
      email: worker.email,
      postal_name: worker.postal_name,
      zipnumber: worker.zipnumber,
      is_available: worker.is_available, 
      is_suspended: worker.is_suspended,
      latitude: latitude,
      longitude: longitude,
    };
  }) || [];

  const columns = useMemo(() => [
    { field: "displayIndex", headerName: "No", width: 50 },
    { field: "id", headerName: "ID", width: 70 },
    { field: "worker_name", headerName: "Technician Name", width: 150 },
    { field: "mobile_number", headerName: "Phone", width: 130 },
    { field: "service_name", headerName: "Services", width: 180 },
    { field: "experience", headerName: "Experience", width: 80 },
    { field: "location", headerName: "Location", width: 130 },
    { field: "district", headerName: "District", width: 120 },
    {
      field: "is_available",
      headerName: "Live Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "ONLINE" : "OFFLINE"}
          size="small"
          style={{
            backgroundColor: params.value ? "#2e7d32" : "#c62828",
            color: "white",
            fontWeight: 'bold'
          }}
        />
      ),
    },
    {
      field: "is_suspended",
      headerName: "Suspend Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value ? "SUSPENDED" : "Active"}
          size="small"
          color={params.value ? "error" : "success"}
          style={{
            color: "white",
            fontWeight: 'bold'
          }}
        />
      ),
    },
    {
      field: "location_link",
      headerName: "Live Location",
      width: 140,
      renderCell: (params) => {
        const { latitude, longitude } = params.row;
        if (!latitude || !longitude) return "N/A";

        const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

        return (
          <Button
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Track ➤
          </Button>
        );
      },
    },
  ], []);

  return (
    <div className="mt-3">
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: '10px',
        gap: '10px'
      }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
          Live Technician Status ({rows.length})
        </Typography>
        <span style={{ 
          fontSize: '12px', 
          padding: '4px 8px', 
          borderRadius: '4px',
          backgroundColor: connectionStatus === 'Connected' ? '#e8f5e9' : '#ffebee',
          color: connectionStatus === 'Connected' ? '#2e7d32' : '#c62828',
          fontWeight: 'bold'
        }}>
          {connectionStatus === 'Connected' ? '🟢 LIVE' : '🔴 OFFLINE'}
        </span>
      </div>
      
      <Box
        sx={{
            flexGrow: 1,
            bgcolor: "background.paper",
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: "0 0px 15px rgba(115, 230, 124,1)",
            marginBottom: '20px',
          }}
      >
        <div>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 25, 50]} 
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'is_available', sort: 'desc' }] }, 
            }}
            disableSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-cell': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
              },
            }}
          />
        </div>
      </Box>
    </div>
  );
};

export default RealTimeServiceManDataGrid;